import { Tab, useGetOne } from "react-admin";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DialogProps } from "../misc/DialogProps";
import { formatDate } from "../utils/calendar";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Tabs } from "../misc/Tabs";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { EventDialog } from "./EventDialog";
import { useDialog } from "../hooks/useDialog";
import { useState } from "react";
import { AddStudentToCourseButton } from "../buttons/AddStudentToCourseButton";
import { DateTime } from "luxon";
import { BKFCourse, Course, CourseEvent, isTheoryCourse } from "../providers/coursesProvider";
import { CourseEventsList } from "./CourseDetailsDialog/CourseEventsList";
import { CourseEventsCalendar } from "./CourseDetailsDialog/CourseEventsCalendar";
import { CourseAttendeesList } from "./CourseDetailsDialog/CourseAttendeesList";
import { useMenu } from "../hooks/useMenu";
import { DialogTopRightActions } from "../misc/DialogTopRightActions";
import { ConfirmDialog } from "./ConfirmDialog";
import { DateWithoutTime } from "../model/DateWithoutTime";
import { EditCourseDialog } from "./EditCourseDialog";
import { CloneCourseDialog } from "./CloneCourseDialog";
import { AddTheoryLessonToCourseButton } from "../buttons/AddTheoryLessonToCourseButton";
import { deleteEvent } from "../api/backoffice.api";
import { AddCourseParticipantForm } from "./AddCourseParticipantForm";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import DownloadIcon from "@mui/icons-material/Download";
import { CreateCourseAutoFormsDialog } from "./CourseDetailsDialog/CreateCourseAutoFormsDialog";
import { grants } from "../backoffice.access_control";

interface Course_ {
  id: string;
  drivingSchoolId: string;
  name: string;
  start: DateTime;
  end: DateTime;
  isBKFCourse: boolean;
  _initiallyShownDate?: DateWithoutTime;
}

export function CourseDetailsDialog(props: { course: Course_; onCloned: (clone: Course) => void } & DialogProps) {
  const {
    course: { id, drivingSchoolId, isBKFCourse, _initiallyShownDate },
    onCloned,
    ...dialogProps
  } = props;
  const [selectedEvent, setSelectedEvent] = useState<CourseEvent | null>(null);
  const { dialogProps: eventDialogProps, openDialog: openEventDialog } = useDialog();
  // We fetch the given course here via useGetOne so that we rerender whenever the course changes ...
  const { data: course } = useGetOne<Course>("courses", { id, meta: { drivingSchoolId, isBKFCourse } });
  const { name, start, end } = course ?? props.course;
  const [activeTab, setActiveTab] = useState("Wochenplanung");
  const [showAddCourseParticipantForm, setShowAddCourseParticipantForm] = useState(false);
  const { dialogProps: autoFormsDialogProps, openDialog: openAutoFormsDialog } = useDialog();
  const canCreateCourseAutoForms =
    !!course?.isBKFCourse && grants.has("Student:getFilledOutForm") && grants.has("DrivingSchool:AutoForm:list");
  return (
    <>
      <Dialog
        {...dialogProps}
        fullWidth
        maxWidth={isBKFCourse ? "xl" : "lg"}
        PaperProps={{ sx: { height: "calc(100dvh - 64px)" } }}
      >
        <DialogTitle>
          {name} vom {formatDate(start)} bis {formatDate(end)}
        </DialogTitle>
        <DialogCloseButton onClick={dialogProps.onClose} />
        {course && <_MoreMenu course={course} onCloned={onCloned} closeCourseDetailsDialog={dialogProps.onClose} />}
        <DialogContent>
          {!course && (
            <div style={{ height: "300px" }}>
              <LoadingIndicator />
            </div>
          )}
          {course && (
            <Tabs syncWithLocation={false} onTabChange={(activeTab) => setActiveTab(activeTab.props.label)}>
              <Tab label="Wochenplanung">
                <CourseEventsCalendar course={course} initiallyShownDate={_initiallyShownDate} />
              </Tab>
              <Tab label="Termine">
                <CourseEventsList
                  course={course}
                  openEvent={(event) => {
                    setSelectedEvent(event);
                    openEventDialog();
                    return false;
                  }}
                />
              </Tab>
              <Tab label="Teilnehmer">
                {showAddCourseParticipantForm ? (
                  <AddCourseParticipantForm
                    course={course as BKFCourse}
                    onCancel={() => setShowAddCourseParticipantForm(false)}
                    onSuccess={() => setShowAddCourseParticipantForm(false)}
                  />
                ) : (
                  <CourseAttendeesList course={course} />
                )}
              </Tab>
            </Tabs>
          )}
        </DialogContent>
        <DialogActions>
          {isTheoryCourse(course) && activeTab === "Termine" && <AddTheoryLessonToCourseButton course={course} />}
          {showAddCourseParticipantForm &&
            activeTab === "Teilnehmer" &&
            location.hostname === "backoffice.autovio.de" && (
              <Button
                startIcon={<NavigateBeforeIcon />}
                variant="outlined"
                onClick={() => setShowAddCourseParticipantForm(false)}
              >
                Zurück
              </Button>
            )}
          {!showAddCourseParticipantForm && activeTab === "Teilnehmer" && course && (
            <>
              {canCreateCourseAutoForms && (
                <Button
                  variant="outlined"
                  sx={{ height: "40px" }}
                  startIcon={<DownloadIcon />}
                  onClick={openAutoFormsDialog}
                >
                  Formularerstellung starten
                </Button>
              )}
              <AddStudentToCourseButton
                course={course}
                onClick={course.isBKFCourse ? () => setShowAddCourseParticipantForm(true) : undefined}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
      {selectedEvent && (
        <EventDialog event={selectedEvent} isOpen={eventDialogProps.open} onClose={eventDialogProps.onClose} />
      )}
      {course?.isBKFCourse && canCreateCourseAutoForms && (
        <CreateCourseAutoFormsDialog course={course} {...autoFormsDialogProps} />
      )}
    </>
  );
}

function _MoreMenu(props: { course: Course; onCloned: (clone: Course) => void; closeCourseDetailsDialog: () => void }) {
  const { course, onCloned, closeCourseDetailsDialog } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const { dialogProps: editDialogProps, openDialog: openEditDialog } = useDialog();
  const { dialogProps: cloneDialogProps, openDialog: openCloneDialog } = useDialog();
  const { dialogProps: confirmDeleteDialogProps, openDialog: openConfirmDeleteDialog } = useDialog();
  const [isGeneratingSchedulePdf, setIsGeneratingSchedulePdf] = useState(false);

  const openSchedulePdf = async () => {
    setIsGeneratingSchedulePdf(true);
    closeMenu();
    const pdfWindow = window.open("", "_blank");
    await runSafely(
      async () => {
        const { generateCourseSchedulePdf } = await import("./CourseDetailsDialog/courseSchedulePdf");
        const pdf = await generateCourseSchedulePdf(course);
        const pdfBytes = new Uint8Array(pdf);
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const pdfObjectUrl = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = pdfObjectUrl;
        } else {
          window.open(pdfObjectUrl, "_blank");
        }
        window.setTimeout(() => URL.revokeObjectURL(pdfObjectUrl), 60_000);
      },
      { errorMessage: "Fehler beim Erstellen des Stundenplans.", successMessage: "" },
    );
    setIsGeneratingSchedulePdf(false);
  };
  return (
    <>
      <DialogTopRightActions>
        {isGeneratingSchedulePdf ? (
          <CircularProgress size={24} />
        ) : (
          <IconButton onClick={openMenu}>
            <MoreVertIcon />
          </IconButton>
        )}
      </DialogTopRightActions>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem onClick={openSchedulePdf}>PDF-Stundenplan erstellen</MenuItem>
        {course.isBKFCourse && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openEditDialog();
            }}
          >
            Kurs bearbeiten
          </MenuItem>
        )}
        {course.isBKFCourse && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openCloneDialog();
            }}
          >
            Kurs duplizieren
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            closeMenu();
            openConfirmDeleteDialog();
          }}
        >
          Kurs löschen
        </MenuItem>
      </Menu>
      <EditCourseDialog course={course} {...editDialogProps} />
      <CloneCourseDialog course={course} onCloned={onCloned} {...cloneDialogProps} />
      <ConfirmDialog
        {...confirmDeleteDialogProps}
        confirmButtonLabel="Ja, Kurs löschen"
        title="Kurs löschen?"
        message="Bist du dir sicher, dass der Kurs gelöscht werden soll? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={async () => {
          await runSafely<any>(
            () => {
              if (course.isTheoryCourse) {
                return Promise.all(course.events.map((event) => deleteEvent({ calendarEventUid: event.id })));
              } else {
                return dataProvider.delete("courses", { id: course.id });
              }
            },
            {
              errorMessage: "Fehler beim Löschen des Kurses.",
              successMessage: "Kurs erfolgreich gelöscht.",
              onSuccess: closeCourseDetailsDialog,
            },
          );
        }}
      />
    </>
  );
}
